import { useCallback, useMemo } from 'react'
import { useTableColumnPrefsStore } from '@/providers/TableColumnPrefsProvider'
import { nextSort, type SortState } from '@/lib/tableSort'
import {
  clearColumnWidth,
  isColumnVisible,
  isCustomized,
  resolveTableColumns,
  seedDefaultHidden,
  setColumnSort,
  setColumnVisible,
  setColumnWidth,
  type ResolvedColumn,
  type TableColumnDef,
} from '@/lib/tableColumnPrefs'

export interface UseTableColumnsResult {
  /** Every declared column, with the user's visibility and width applied. */
  all: ResolvedColumn[]
  /** Just the visible ones, in declaration order. */
  visible: ResolvedColumn[]
  /** Whether one column renders. The predicate to guard a cell with. */
  isVisible: (id: string) => boolean
  /** Effective width in px, or undefined to let the browser lay it out. */
  widthOf: (id: string) => number | undefined
  /** True when that width was dragged by the user, not merely declared as a default. */
  isPinned: (id: string) => boolean
  setVisible: (id: string, visible: boolean) => void
  toggle: (id: string) => void
  setWidth: (id: string, px: number) => void
  resetWidth: (id: string) => void
  /** Back to the declared defaults, forgetting the stored key entirely. */
  reset: () => void
  /** True when the user has hidden or resized anything here. */
  customized: boolean
  hiddenCount: number
  /**
   * The column the table is sorted by, or null for the table's own order
   * (z8nrz7e4h6). Only a `sortable` column that still exists can be it.
   */
  sort: SortState | null
  /** Header click: ascending → descending → unsorted. */
  cycleSort: (id: string) => void
  /** Whether a column offers sorting. */
  isSortable: (id: string) => boolean
}

/**
 * Per-user column visibility and width for one table.
 *
 * The single entry point every table in the app uses. A table declares its
 * columns once as a module-level constant and passes them in with a stable
 * `tableId`; everything else — loading the saved layout from the backend,
 * writing changes back, keeping two mounts of the same table in step — is
 * handled by the provider underneath.
 *
 *     const COLUMNS: TableColumnDef[] = [
 *       { id: 'label', header: 'Label', locked: true },
 *       { id: 'systemName', header: 'System name' },
 *       { id: 'actions', header: 'Actions', locked: true },
 *     ]
 *
 *     const cols = useTableColumns('field-library', COLUMNS)
 *     // …
 *     {cols.isVisible('systemName') && <TableCell>…</TableCell>}
 *
 * **Pass a module-level constant, not an inline array.** `columns` is a
 * dependency of the memos below, so a fresh array on every render recomputes
 * them every render. Build it with `useMemo` if it genuinely has to be dynamic
 * (a permission-dependent column, say).
 *
 * **Header and body must agree.** Guard the `TableHead` and its `TableCell`
 * with the same `isVisible` call, or the columns shear. `colSpan` on an
 * empty-state row must use `cols.visible.length`, not a hard-coded count.
 */
export function useTableColumns(
  tableId: string,
  columns: TableColumnDef[],
): UseTableColumnsResult {
  const store = useTableColumnPrefsStore()
  const stored = store.prefsFor(tableId)

  // `defaultHidden` is folded in only while nothing is stored for this table, so
  // a default-hidden column the user has since switched on stays on.
  const prefs = useMemo(
    () => seedDefaultHidden(columns, stored, isCustomized(stored)),
    [columns, stored],
  )

  const all = useMemo(() => resolveTableColumns(columns, prefs), [columns, prefs])
  const visible = useMemo(() => all.filter(c => c.visible), [all])

  const byId = useMemo(() => new Map(all.map(c => [c.def.id, c])), [all])

  const isVisible = useCallback((id: string) => byId.get(id)?.visible ?? false, [byId])
  const widthOf = useCallback((id: string) => byId.get(id)?.width, [byId])
  const isPinned = useCallback((id: string) => byId.get(id)?.pinned ?? false, [byId])

  const setVisible = useCallback(
    (id: string, next: boolean) => {
      store.setPrefsFor(tableId, setColumnVisible(columns, prefs, id, next))
    },
    [store, tableId, columns, prefs],
  )

  const toggle = useCallback(
    (id: string) => {
      const def = columns.find(c => c.id === id)
      if (!def) return
      setVisible(id, !isColumnVisible(def, prefs))
    },
    [columns, prefs, setVisible],
  )

  const setWidth = useCallback(
    (id: string, px: number) => {
      store.setPrefsFor(tableId, setColumnWidth(prefs, id, px))
    },
    [store, tableId, prefs],
  )

  const resetWidth = useCallback(
    (id: string) => {
      store.setPrefsFor(tableId, clearColumnWidth(prefs, id))
    },
    [store, tableId, prefs],
  )

  const reset = useCallback(() => store.resetTable(tableId), [store, tableId])

  const isSortable = useCallback((id: string) => byId.get(id)?.def.sortable === true, [byId])
  const sort = useMemo<SortState | null>(
    () => (prefs.sort && isSortable(prefs.sort.id) ? prefs.sort : null),
    [prefs.sort, isSortable],
  )
  const cycleSort = useCallback(
    (id: string) => {
      if (!isSortable(id)) return
      store.setPrefsFor(tableId, setColumnSort(prefs, nextSort(sort, id)))
    },
    [store, tableId, prefs, sort, isSortable],
  )

  return {
    all,
    visible,
    isVisible,
    widthOf,
    isPinned,
    setVisible,
    toggle,
    setWidth,
    resetWidth,
    reset,
    customized: isCustomized(prefs),
    hiddenCount: all.filter(c => !c.visible).length,
    sort,
    cycleSort,
    isSortable,
  }
}
